import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import queryString from "query-string";
import { toast } from "react-toastify";
import financeApiService from "../../../../services/financeApiService";
import financeV2Adapters from "../../../../adapters/financeV2Adapters";
import tools from "../../../../helpers/tools";

// Aligné sur CustomPagination (itemsCountPerPage figé à 15).
const PER_PAGE = 15;

const initialFilters = {
  devisReference: "",
  statut: "",
  financeurUuid: "",
  typeFinancement: "",
  dateDebut: "",
  dateFin: "",
  // LS-634 — "" (tous) / "1" (échéancier OK) / "0" (échéancier incomplet).
  echeancierRenseigne: "",
  // LS-716 — recherche partielle sur le n° de contrat financeur.
  numeroContrat: "",
  // LS-717 — recherche partielle sur le compte comptable du dossier.
  compteComptable: "",
  // LS-718 — recherche partielle sur les immatriculations du dossier.
  immatriculation: "",
  // LS-717 — "" (date de création décroissante) / "compte_comptable" (compte
  // le plus récent en tête) / "compte_comptable_asc" (le plus ancien en tête).
  // Persisté avec les filtres (URL + localStorage).
  tri: "",
};

const initialMeta = {
  current_page: 1,
  per_page: PER_PAGE,
  total: 0,
  last_page: 1,
};

// LS-745 — rail de persistance des filtres, calqué sur la flotte :
// l'URL (partageable, survivant au bouton précédent) prime sur le
// localStorage, qui sert de repli au prochain passage sur la liste.
const STORAGE_KEY_FILTERS = "financements_filters";
const FILTER_KEYS = Object.keys(initialFilters);

export const filtersToUrlParams = (filters, page) => {
  const params = {};
  FILTER_KEYS.forEach((key) => {
    const value = filters[key];
    if (value !== null && value !== undefined && value !== "") {
      params[key] = value;
    }
  });
  if (page > 1) params.page = String(page);
  return params;
};

export const urlParamsToState = (search) => {
  const raw = queryString.parse(search);
  const filters = {};
  FILTER_KEYS.forEach((key) => {
    if (typeof raw[key] === "string" && raw[key] !== "") {
      filters[key] = raw[key];
    }
  });
  const page = parseInt(raw.page, 10);
  const hasPage = !Number.isNaN(page) && page > 1;
  if (Object.keys(filters).length === 0 && !hasPage) return null;
  return {
    filters: { ...initialFilters, ...filters },
    page: hasPage ? page : 1,
  };
};

const saveFiltersToStorage = (filters) => {
  try {
    const diffs = {};
    FILTER_KEYS.forEach((key) => {
      if (filters[key]) diffs[key] = filters[key];
    });
    if (Object.keys(diffs).length > 0) {
      localStorage.setItem(STORAGE_KEY_FILTERS, JSON.stringify(diffs));
    } else {
      localStorage.removeItem(STORAGE_KEY_FILTERS);
    }
  } catch (e) {
    // Silently fail
  }
};

const loadFiltersFromStorage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_FILTERS);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    const filters = {};
    FILTER_KEYS.forEach((key) => {
      if (typeof parsed[key] === "string" && parsed[key] !== "") {
        filters[key] = parsed[key];
      }
    });
    return Object.keys(filters).length > 0
      ? { ...initialFilters, ...filters }
      : null;
  } catch (e) {
    return null;
  }
};

const useFinanceList = () => {
  const history = useHistory();
  const location = useLocation();

  // État initial : URL > localStorage > défauts (résolu une seule fois).
  const [initialState] = useState(() => {
    const fromUrl = urlParamsToState(location.search);
    if (fromUrl) return fromUrl;
    const storedFilters = loadFiltersFromStorage();
    if (storedFilters) return { filters: storedFilters, page: 1 };
    return { filters: initialFilters, page: 1 };
  });

  const [filters, setFilters] = useState(initialState.filters);
  const [page, setPage] = useState(initialState.page);
  const [demandes, setDemandes] = useState([]);
  const [meta, setMeta] = useState(initialMeta);
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef(null);

  const fetchDemandes = useCallback(async () => {
    setIsLoading(true);
    try {
      const query = tools.getSearchString({
        ...financeV2Adapters.toApi.filtresDemandes(filters),
        par_page: PER_PAGE,
        page,
      });
      const res = await financeApiService.listerDemandes(query);
      const items = res.data?.data || [];
      setDemandes(items.map(financeV2Adapters.fromApi.demandeListItem));
      setMeta(res.data?.meta || { ...initialMeta, current_page: page });
    } catch (err) {
      console.error("Erreur chargement des demandes", err);
      toast.error("Erreur lors du chargement des demandes.");
    } finally {
      setIsLoading(false);
    }
  }, [filters, page]);

  // Débounce (350 ms) sur tout changement de filtre ou de page.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchDemandes, 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [fetchDemandes]);

  // LS-745 — refléter filtres et page dans l'URL (history.replace : pas
  // d'entrée d'historique par frappe) et dans le localStorage.
  useEffect(() => {
    saveFiltersToStorage(filters);
    const newSearch = queryString.stringify(filtersToUrlParams(filters, page));
    const currentSearch = location.search.replace(/^\?/, "");
    if (newSearch !== currentSearch) {
      history.replace(newSearch ? `?${newSearch}` : location.pathname);
    }
  }, [filters, page, history, location.pathname, location.search]);

  const updateFilter = useCallback((key, value) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const resetFilters = useCallback(() => {
    setPage(1);
    setFilters(initialFilters);
  }, []);

  return {
    filters,
    updateFilter,
    resetFilters,
    page,
    setPage,
    demandes,
    meta,
    isLoading,
  };
};

export default useFinanceList;
